import React, { useEffect, useRef, useState } from 'react';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import {
  getMyEmailPreferences,
  patchMyEmailPreferences,
} from '../api/emailPreferences';
import { FRECUENCIAS, FRECUENCIA_ORDEN } from '../utils/notificaciones';

// "Lo que te falta" is not a yes/no list: the person picks how often. The
// select writes the cadence AND the list flag together (nunca = opted out),
// so every other sender that only reads the flag stays correct.
const LISTA_RESUMEN = 'resumen_agente';
const SIN_ELEGIR = '';

// Debounced save: rapid toggling produces one PATCH per 500ms quiet
// window. Matches the spec from the issue.
const SAVE_DEBOUNCE_MS = 500;

const ConfiguracionCorreo = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const [lists, setLists] = useState([]); // [{key, displayName, description, transactional}]
  const [preferences, setPreferences] = useState({}); // {[listKey]: bool}
  const [unsubscribedAll, setUnsubscribedAll] = useState(false);
  const [frecuencia, setFrecuencia] = useState(SIN_ELEGIR); // '' = org default
  const [frecuenciaOrg, setFrecuenciaOrg] = useState('semanal');

  // Pending diff against last-saved state. Coalesces multiple toggles
  // into a single PATCH so we don't write per-keystroke.
  const pendingRef = useRef({ preferences: {}, unsubscribedAll: null, frecuenciaResumen: undefined });
  const debounceTimerRef = useRef(null);

  // Initial load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getMyEmailPreferences();
        if (cancelled) return;
        setLists(data.lists || []);
        setPreferences(data.preferences || {});
        setUnsubscribedAll(Boolean(data.unsubscribedAll));
        setFrecuencia(data.frecuenciaResumen || SIN_ELEGIR);
        if (data.frecuenciaOrg) setFrecuenciaOrg(data.frecuenciaOrg);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err.message || 'Error al cargar las preferencias');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Flush any pending changes on unmount so a quick navigate-away
  // doesn't drop the user's last toggle.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      const pending = pendingRef.current;
      const hasAny =
        Object.keys(pending.preferences).length > 0 ||
        pending.unsubscribedAll !== null ||
        pending.frecuenciaResumen !== undefined;
      if (hasAny) {
        // Fire and forget; component is unmounting.
        patchMyEmailPreferences(buildPatchBody(pending)).catch(() => {});
      }
    };
  }, []);

  const buildPatchBody = (pending) => {
    const body = {};
    if (Object.keys(pending.preferences).length > 0) {
      body.preferences = { ...pending.preferences };
    }
    if (pending.unsubscribedAll !== null) {
      body.unsubscribedAll = pending.unsubscribedAll;
    }
    if (pending.frecuenciaResumen !== undefined) {
      body.frecuenciaResumen = pending.frecuenciaResumen;
    }
    return body;
  };

  const scheduleSave = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      const pending = pendingRef.current;
      const hasAny =
        Object.keys(pending.preferences).length > 0 ||
        pending.unsubscribedAll !== null ||
        pending.frecuenciaResumen !== undefined;
      if (!hasAny) return;
      const body = buildPatchBody(pending);
      // Reset before the write so concurrent toggles during the request
      // accumulate fresh.
      pendingRef.current = { preferences: {}, unsubscribedAll: null, frecuenciaResumen: undefined };
      setSaving(true);
      setSaveError(null);
      try {
        await patchMyEmailPreferences(body);
        setSavedAt(new Date());
      } catch (err) {
        setSaveError(err.message || 'Error al guardar');
        // Re-merge so the next debounce retries; user changes since then
        // win over the rollback values.
        pendingRef.current = {
          preferences: { ...body.preferences, ...pendingRef.current.preferences },
          unsubscribedAll:
            pendingRef.current.unsubscribedAll !== null
              ? pendingRef.current.unsubscribedAll
              : body.unsubscribedAll ?? null,
          frecuenciaResumen:
            pendingRef.current.frecuenciaResumen !== undefined
              ? pendingRef.current.frecuenciaResumen
              : body.frecuenciaResumen,
        };
      } finally {
        setSaving(false);
      }
    }, SAVE_DEBOUNCE_MS);
  };

  const handleToggle = (listKey, nextValue) => {
    setPreferences((prev) => ({ ...prev, [listKey]: nextValue }));
    pendingRef.current.preferences[listKey] = nextValue;
    scheduleSave();
  };

  const handleFrecuencia = (value) => {
    setFrecuencia(value);
    // '' clears the choice (back to the org default); 'nunca' also opts out
    // of the list; anything else opts back in.
    pendingRef.current.frecuenciaResumen = value === SIN_ELEGIR ? null : value;
    const acepta = value !== 'nunca';
    setPreferences((prev) => ({ ...prev, [LISTA_RESUMEN]: acepta }));
    pendingRef.current.preferences[LISTA_RESUMEN] = acepta;
    scheduleSave();
  };

  const handleToggleAll = (nextValue) => {
    setUnsubscribedAll(nextValue);
    pendingRef.current.unsubscribedAll = nextValue;
    scheduleSave();
  };

  if (loading) {
    return (
      <div>
        <Heading level={2}>Preferencias de correo</Heading>
        <Text className="mt-4">Cargando…</Text>
      </div>
    );
  }

  if (loadError) {
    return (
      <div>
        <Heading level={2}>Preferencias de correo</Heading>
        <Text className="mt-4 text-red-600">{loadError}</Text>
      </div>
    );
  }

  const nonTransactional = lists.filter((l) => !l.transactional);
  const transactional = lists.filter((l) => l.transactional);

  return (
    <div className="space-y-8">
      <div>
        <Heading level={2}>Preferencias de correo</Heading>
        <Text className="mt-2 text-gray-600">
          Elige qué tipos de correos quieres recibir de Surelia. Tus cambios se guardan automáticamente.
        </Text>
        <div className="mt-2 text-sm text-gray-500" aria-live="polite">
          {saving
            ? 'Guardando…'
            : saveError
              ? <span className="text-red-600">No se pudo guardar: {saveError}</span>
              : savedAt
                ? `Guardado a las ${savedAt.toLocaleTimeString('es-MX')}`
                : ''}
        </div>
      </div>

      {/* Master kill switch */}
      <div className="rounded-lg border border-azul-surelia/20 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-semibold">Pausar todos los correos no transaccionales</div>
            <Text className="mt-1 text-sm text-gray-600">
              Al activar esto dejarás de recibir todos los correos opcionales. Los correos
              de facturación y seguridad seguirán llegando.
            </Text>
          </div>
          <label className="inline-flex shrink-0 cursor-pointer items-center">
            <input
              type="checkbox"
              checked={unsubscribedAll}
              onChange={(e) => handleToggleAll(e.target.checked)}
              className="h-5 w-5 accent-azul-surelia"
            />
          </label>
        </div>
      </div>

      {/* Per-list toggles */}
      <div className="space-y-3">
        <div className="font-semibold">Listas opcionales</div>
        {nonTransactional.map((list) => {
          const enabled = preferences[list.key] !== false;
          const disabled = unsubscribedAll;
          if (list.key === LISTA_RESUMEN) {
            // Opted out through the old checkbox but no cadence chosen: show
            // it as "No recibir" so the control tells the truth.
            const valor = frecuencia === SIN_ELEGIR && !enabled ? 'nunca' : frecuencia;
            return (
              <div
                key={list.key}
                className={`flex items-start justify-between gap-4 rounded-lg border p-4 ${
                  disabled ? 'opacity-60' : ''
                }`}
              >
                <div>
                  <div className="font-medium">{list.displayName}</div>
                  <Text className="mt-1 text-sm text-gray-600">{list.description}</Text>
                </div>
                <select
                  value={valor}
                  disabled={disabled}
                  onChange={(e) => handleFrecuencia(e.target.value)}
                  className="shrink-0 rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia"
                >
                  <option value={SIN_ELEGIR}>
                    Como mi organización ({(FRECUENCIAS[frecuenciaOrg]?.label || 'Una vez a la semana').toLowerCase()})
                  </option>
                  {FRECUENCIA_ORDEN.map((f) => (
                    <option key={f} value={f}>{FRECUENCIAS[f].label}</option>
                  ))}
                </select>
              </div>
            );
          }
          return (
            <div
              key={list.key}
              className={`flex items-start justify-between gap-4 rounded-lg border p-4 ${
                disabled ? 'opacity-60' : ''
              }`}
            >
              <div>
                <div className="font-medium">{list.displayName}</div>
                <Text className="mt-1 text-sm text-gray-600">{list.description}</Text>
              </div>
              <label className="inline-flex shrink-0 cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={enabled && !disabled}
                  disabled={disabled}
                  onChange={(e) => handleToggle(list.key, e.target.checked)}
                  className="h-5 w-5 accent-azul-surelia"
                />
              </label>
            </div>
          );
        })}
      </div>

      {/* Transactional read-only */}
      {transactional.length > 0 && (
        <div className="space-y-3">
          <div className="font-semibold">Siempre activas</div>
          {transactional.map((list) => (
            <div
              key={list.key}
              className="flex items-start justify-between gap-4 rounded-lg border bg-gray-50 p-4"
            >
              <div>
                <div className="font-medium">{list.displayName}</div>
                <Text className="mt-1 text-sm text-gray-600">{list.description}</Text>
              </div>
              <span className="shrink-0 rounded-full bg-azul-surelia/10 px-3 py-1 text-xs font-semibold text-azul-surelia">
                Siempre activa
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConfiguracionCorreo;
